import * as React from 'react';
import { Columns3, RefreshCw, Rows3, Search } from 'lucide-react';
import { Button } from '../../primitives/button.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../primitives/dropdown-menu.js';
import { Input } from '../../primitives/input.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../primitives/tooltip.js';
import { useDensity } from '../../use-appearance.js';
import { setDensity } from '../../theme-mode.js';
import type { ListState, ResourceColumn } from '../types.js';

export interface ToolbarProps<TItem> {
  columns: readonly ResourceColumn<TItem>[];
  hidden: ReadonlySet<string>;
  onToggleColumn: (id: string) => void;
  state: ListState;
  onChange: (patch: Partial<ListState>) => void;
  searchPlaceholder?: string;
  showSearch: boolean;
  onRefresh: () => void;
  refreshing: boolean;
  children?: React.ReactNode;
}

export function Toolbar<TItem>({
  columns,
  hidden,
  onToggleColumn,
  state,
  onChange,
  searchPlaceholder,
  showSearch,
  onRefresh,
  refreshing,
  children,
}: ToolbarProps<TItem>) {
  const density = useDensity();
  const [draft, setDraft] = React.useState(state.q ?? '');

  // The URL is the source of truth; typing must not push a history entry per
  // keystroke, so the input keeps a draft and commits on a pause.
  React.useEffect(() => {
    setDraft(state.q ?? '');
  }, [state.q]);

  React.useEffect(() => {
    const current = state.q ?? '';
    if (draft === current) return;
    const timer = setTimeout(() => onChange({ q: draft || undefined }), 300);
    return () => clearTimeout(timer);
  }, [draft, state.q, onChange]);

  const hideable = columns.filter((column) => column.hideable !== false);

  return (
    <div data-slot="toolbar" className="flex flex-wrap items-center gap-2">
      {showSearch && (
        <div className="relative min-w-56 flex-1 sm:max-w-80">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-faint" />
          <Input
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={searchPlaceholder ?? 'Cerca…'}
            className="pl-9"
            aria-label="Cerca"
          />
        </div>
      )}

      {children}

      <div className="ml-auto flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Aggiorna"
              onClick={onRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={refreshing ? 'animate-spin' : undefined} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Aggiorna</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={density === 'compact' ? 'Densità comoda' : 'Densità compatta'}
              aria-pressed={density === 'compact'}
              onClick={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')}
            >
              <Rows3 />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {density === 'compact' ? 'Densità comoda' : 'Densità compatta'}
          </TooltipContent>
        </Tooltip>

        {hideable.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Colonne">
                <Columns3 />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Colonne</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {hideable.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={!hidden.has(column.id)}
                  onCheckedChange={() => onToggleColumn(column.id)}
                  onSelect={(event) => event.preventDefault()}
                >
                  {column.header}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
